/**
 * AppHeader CTA registry.
 *
 * Pages mounted inside `AppShell` can call `useAppHeaderCTA(...)` to surface
 * a single primary action (e.g. "+ New product") in the V2 top header. The
 * registration is a no-op when the provider is absent, so legacy chrome
 * keeps rendering nothing extra.
 *
 * The hook keeps the rendered button stable across closure changes by
 * routing every click through the latest registration via a ref. Page-level
 * code can pass an inline `onClick` without thrashing the header.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";

export type AppHeaderCTA = {
  label: string;
  /** Optional click handler. If omitted, `href` is required. */
  onClick?: () => void;
  /** Optional href; renders an anchor instead of a button. */
  href?: string;
  /** Lucide icon component or any matching `{ className?: string }` component. */
  icon?: ComponentType<{ className?: string }>;
};

type AppHeaderContextValue = {
  cta: AppHeaderCTA | null;
  setCTA: (cta: AppHeaderCTA | null) => void;
};

const AppHeaderContext = createContext<AppHeaderContextValue>({
  cta: null,
  setCTA: () => {},
});

export function AppHeaderProvider({ children }: { children: ReactNode }) {
  const [cta, setCTA] = useState<AppHeaderCTA | null>(null);
  const value = useMemo<AppHeaderContextValue>(() => ({ cta, setCTA }), [cta]);
  return <AppHeaderContext.Provider value={value}>{children}</AppHeaderContext.Provider>;
}

/**
 * Read the currently-registered CTA. Used by `AppHeader` to render it.
 */
export function useAppHeaderCTAValue(): AppHeaderCTA | null {
  return useContext(AppHeaderContext).cta;
}

/**
 * Register a CTA for the lifetime of the calling component. Pass `null` to
 * clear (e.g. while a page sub-view shouldn't expose the action).
 *
 * Identity rules:
 *  - The registered button re-mounts when `label`, `icon`, or `href` change.
 *  - `onClick` is routed through a latest-callback ref, so inline closures
 *    are safe — the header always invokes the freshest handler.
 */
export function useAppHeaderCTA(cta: AppHeaderCTA | null): void {
  const { setCTA } = useContext(AppHeaderContext);
  const ctaRef = useRef(cta);
  ctaRef.current = cta;

  const stableOnClick = useCallback(() => {
    ctaRef.current?.onClick?.();
  }, []);

  const label = cta?.label ?? null;
  const icon = cta?.icon ?? null;
  const href = cta?.href ?? null;
  const hasOnClick = !!cta?.onClick;

  useEffect(() => {
    if (label == null) {
      setCTA(null);
      return;
    }
    setCTA({
      label,
      ...(icon ? { icon } : {}),
      ...(href ? { href } : {}),
      ...(hasOnClick ? { onClick: stableOnClick } : {}),
    });
  }, [label, icon, href, hasOnClick, setCTA, stableOnClick]);

  // Final cleanup on unmount so a page leaving doesn't leave a stale CTA.
  useEffect(() => () => setCTA(null), [setCTA]);
}
